import { z } from 'zod';
/**
 * One country in the Roamzy catalog. Per D-008 we sell countries, never
 * operators — multiple wholesale operators inside a country roll up to a
 * single retail rate here.
 *
 * The values here come from `product/data/prices.json`, regenerated from the
 * single retail rate sheet by `product/scripts/build-prices.mjs`. RETAIL ONLY —
 * no wholesale/cost field by design (see rateUsdtPerMb). Do not hand-edit.
 */
export const Country = z.object({
    name: z.string().min(1),
    /** ISO 3166-1 alpha-2; null only if we couldn't resolve the source name. */
    isoAlpha2: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .nullable(),
    /** URL-stable slug, always `"esim-" + slugify(name)`. */
    slug: z.string().regex(/^esim-[a-z0-9-]+$/),
    /** Regional-indicator flag emoji, or 🌐 fallback when `isoAlpha2` is null. */
    flag: z.string().min(1),
    /** Source zone label (e.g. "EU 33 REG", "Africa"). Display info only. */
    zone: z.string().min(1),
    /**
     * Retail rate in USDT per megabyte — the customer-facing price and the SINGLE
     * SOURCE OF TRUTH for every surface (site, articles, MCP, agents.json).
     *
     * ⛔ There is deliberately NO cost/wholesale field in this catalog (removed
     * 2026-06-02). It was unused (admin reads actual cost from the ledger,
     * `notes.ventaAmountMicroUsdt`) and only ever leaked — `/api/*` serialized it
     * to anonymous callers. Keep this catalog retail-only; if a cost projection
     * is ever needed, put it in a SEPARATE admin-only file, never here.
     */
    rateUsdtPerMb: z.number().positive(),
    /** Convenience: rateUsdtPerMb * 1024. */
    rateUsdtPerGb: z.number().positive(),
    /** True iff rateUsdtPerMb > 0.05 USDT (D-007 outlier chip). */
    isPremium: z.boolean(),
});
/**
 * Top-level price catalog. `topTourismSorted` is a curated ordered list of
 * slugs (subset of `countries[].slug`) used to seed the homepage.
 */
export const PricesData = z.object({
    /** Stamp from the source xlsx filename, e.g. "16032026". */
    version: z.string().min(1),
    /** ISO-8601 UTC timestamp when the JSON was generated. */
    generatedAt: z.string().datetime({ offset: false }),
    countries: z.array(Country).min(1),
    topTourismSorted: z.array(z.string().regex(/^esim-[a-z0-9-]+$/)),
});
